/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SNAKE_LEVELS, GRID_COLS, GRID_ROWS, DIR, getInitialSnake, spawnItems, shuffleArray, playEatSound, playWrongSound, playLevelUpSound, speakMission } from "@/utils/snakeGameData";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Home, Star, Trophy, Volume2, VolumeX, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import axios from "axios";

import { logger } from "@/utils/logger";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SNAKE_COLORS = ["#4ade80", "#22c55e", "#16a34a", "#15803d"];

const SnakeGame = ({ startLevel = null, onBack }) => {
  const [phase, setPhase] = useState("playing"); // playing, paused, levelUp, gameover, allDone
  const [levels, setLevels] = useState([]);
  const [levelIdx, setLevelIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);

  const [snake, setSnake] = useState(getInitialSnake);
  const [direction, setDirection] = useState(DIR.RIGHT);
  const [items, setItems] = useState([]);
  const [score, setScore] = useState(0);
  const [stars, setStars] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [collectedLetters, setCollectedLetters] = useState([]);
  const [wrongFlash, setWrongFlash] = useState(false);
  const [growAnim, setGrowAnim] = useState(false);

  // Save score
  const [saveName, setSaveName] = useState(() => {
    try { const d = localStorage.getItem('alba_game_players'); if (d) { const p = JSON.parse(d); return p[0] || ""; } } catch {} return "";
  });
  const [scoreSaved, setScoreSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirRef = useRef(DIR.RIGHT);
  const snakeRef = useRef(getInitialSnake());
  const itemsRef = useRef([]);
  const phaseRef = useRef("playing");
  const tickRef = useRef(null);
  const collectedRef = useRef([]);
  const { width, height } = useWindowSize();

  // Setup levels
  useEffect(() => {
    if (startLevel !== null) {
      const lvl = SNAKE_LEVELS.find(l => l.id === startLevel);
      setLevels(lvl ? [lvl] : [...SNAKE_LEVELS]);
    } else {
      setLevels([...SNAKE_LEVELS]);
    }
  }, [startLevel]);

  const currentLevel = levels[levelIdx];
  const currentQ = currentLevel?.questions[qIdx];

  // Init question
  const initQuestion = useCallback(() => {
    const s = getInitialSnake();
    setSnake(s);
    snakeRef.current = s;
    setDirection(DIR.RIGHT);
    dirRef.current = DIR.RIGHT;
    setCollectedLetters([]);
    collectedRef.current = [];
    setFeedback(null);
    setWrongFlash(false);

    if (currentQ && currentLevel) {
      const correctVal = currentLevel.type === "spell_word" ? currentQ.correct : currentQ.correct;
      const newItems = spawnItems(s, currentQ.pool, correctVal, GRID_ROWS, GRID_COLS);
      setItems(newItems);
      itemsRef.current = newItems;
      if (soundOn) setTimeout(() => speakMission(currentQ.mission), 300);
    }
  }, [currentQ, currentLevel, soundOn]);

  useEffect(() => {
    if (currentQ && phase === "playing") {
      initQuestion();
    }
  }, [levelIdx, qIdx, phase, initQuestion]);

  // Game loop
  useEffect(() => {
    if (phase !== "playing" || !currentLevel) return;

    const tick = () => {
      if (phaseRef.current !== "playing") return;
      const dir = dirRef.current;
      const oldSnake = snakeRef.current;
      const head = oldSnake[0];
      const newHead = { r: head.r + dir.r, c: head.c + dir.c };

      // Wall wrap
      if (newHead.r < 0) newHead.r = GRID_ROWS - 1;
      if (newHead.r >= GRID_ROWS) newHead.r = 0;
      if (newHead.c < 0) newHead.c = GRID_COLS - 1;
      if (newHead.c >= GRID_COLS) newHead.c = 0;

      // Self collision
      if (oldSnake.some(s => s.r === newHead.r && s.c === newHead.c)) {
        // Just skip this tick, don't kill
        return;
      }

      let newSnake = [newHead, ...oldSnake];
      let ate = false;

      // Check item collision
      const hitIdx = itemsRef.current.findIndex(it => it.r === newHead.r && it.c === newHead.c);
      if (hitIdx !== -1) {
        const hitItem = itemsRef.current[hitIdx];
        const lvl = levels[levelIdx];
        const q = lvl?.questions[qIdx];

        if (lvl?.type === "spell_word") {
          const nextLetter = q.correct[collectedRef.current.length];
          if (hitItem.value === nextLetter) {
            // Correct letter in sequence
            ate = true;
            const newCollected = [...collectedRef.current, hitItem.value];
            collectedRef.current = newCollected;
            setCollectedLetters(newCollected);
            setScore(prev => prev + 10);
            setGrowAnim(true);
            setTimeout(() => setGrowAnim(false), 300);
            if (soundOn) playEatSound();
            setFeedback({ type: "correct", text: `${hitItem.value} Benar!` });
            setTimeout(() => setFeedback(null), 800);

            // Remove eaten item, spawn new ones
            const remaining = itemsRef.current.filter((_, i) => i !== hitIdx);
            if (newCollected.length >= q.correct.length) {
              // Word complete!
              itemsRef.current = remaining;
              setItems(remaining);
              setStars(prev => prev + 1);
              if (soundOn) playLevelUpSound();
              setFeedback({ type: "complete", text: `${q.correct.join("")} Berhasil!` });
              // Next question after delay
              setTimeout(() => {
                if (qIdx + 1 < lvl.questions.length) {
                  setQIdx(prev => prev + 1);
                } else {
                  // Level complete
                  if (levelIdx + 1 < levels.length) {
                    phaseRef.current = "levelUp";
                    setPhase("levelUp");
                  } else {
                    phaseRef.current = "allDone";
                    setPhase("allDone");
                    recordGameWon("snake");
                  }
                }
              }, 1200);
            } else {
              // Spawn new items for next letter
              const nextCorrect = q.correct[newCollected.length];
              const newItems = spawnItems(newSnake, q.pool, nextCorrect, GRID_ROWS, GRID_COLS, remaining);
              itemsRef.current = [...remaining, ...newItems];
              setItems([...remaining, ...newItems]);
            }
          } else {
            // Wrong letter - show which one was correct
            const nextCorrect = q.correct[collectedRef.current.length];
            if (soundOn) playWrongSound();
            setFeedback({ type: "wrong", text: `Bukan ${hitItem.value}! Yang benar: ${nextCorrect}` });
            setWrongFlash(true);
            setTimeout(() => { setFeedback(null); setWrongFlash(false); }, 2500);
            setScore(prev => Math.max(0, prev - 5));
            // Remove wrong item
            const remaining = itemsRef.current.filter((_, i) => i !== hitIdx);
            itemsRef.current = remaining;
            setItems(remaining);
          }
        } else {
          // Single letter/number/math
          if (hitItem.value === q.correct) {
            ate = true;
            setScore(prev => prev + 10);
            setStars(prev => prev + 1);
            setGrowAnim(true);
            setTimeout(() => setGrowAnim(false), 300);
            if (soundOn) playEatSound();
            setFeedback({ type: "correct", text: `${hitItem.value} Benar!` });

            const remaining = itemsRef.current.filter((_, i) => i !== hitIdx);
            itemsRef.current = remaining;
            setItems(remaining);

            setTimeout(() => {
              setFeedback(null);
              if (qIdx + 1 < lvl.questions.length) {
                setQIdx(prev => prev + 1);
              } else {
                if (levelIdx + 1 < levels.length) {
                  phaseRef.current = "levelUp";
                  setPhase("levelUp");
                } else {
                  phaseRef.current = "allDone";
                  setPhase("allDone");
                  recordGameWon("snake");
                }
              }
            }, 1000);
          } else {
            if (soundOn) playWrongSound();
            setFeedback({ type: "wrong", text: `Bukan ${hitItem.value}! Jawaban: ${q.correct}` });
            setWrongFlash(true);
            setTimeout(() => { setFeedback(null); setWrongFlash(false); }, 2500);
            setScore(prev => Math.max(0, prev - 5));
            const remaining = itemsRef.current.filter((_, i) => i !== hitIdx);
            itemsRef.current = remaining;
            setItems(remaining);
          }
        }
      }

      // Trim tail unless ate
      if (!ate) newSnake.pop();
      snakeRef.current = newSnake;
      setSnake([...newSnake]);
    };

    const speed = currentLevel?.speed || 300;
    tickRef.current = setInterval(tick, speed);
    return () => clearInterval(tickRef.current);
  }, [phase, currentLevel, levelIdx, qIdx, levels, soundOn]);

  // Sync phase ref
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Direction change
  const changeDir = useCallback((newDir) => {
    const cur = dirRef.current;
    // Prevent 180 turn
    if (cur.r + newDir.r === 0 && cur.c + newDir.c === 0) return;
    dirRef.current = newDir;
    setDirection(newDir);
  }, []);

  // Keyboard controls
  useEffect(() => {
    const handleKey = (e) => {
      if (phase !== "playing") return;
      if (e.key === "ArrowUp") changeDir(DIR.UP);
      else if (e.key === "ArrowDown") changeDir(DIR.DOWN);
      else if (e.key === "ArrowLeft") changeDir(DIR.LEFT);
      else if (e.key === "ArrowRight") changeDir(DIR.RIGHT);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [changeDir, phase]);

  // Level up
  const goNextLevel = () => {
    setLevelIdx(prev => prev + 1);
    setQIdx(0);
    setPhase("playing");
    phaseRef.current = "playing";
  };

  // Restart
  const restartGame = () => {
    setLevelIdx(0);
    setQIdx(0);
    setScore(0);
    setStars(0);
    setCollectedLetters([]);
    collectedRef.current = [];
    setSaveName("");
    setScoreSaved(false);
    setPhase("playing");
    phaseRef.current = "playing";
  };

  // Pause/Resume
  const togglePause = () => {
    if (phase === "playing") {
      setPhase("paused");
      phaseRef.current = "paused";
    } else if (phase === "paused") {
      setPhase("playing");
      phaseRef.current = "playing";
    }
  };

  // Save score
  const handleSaveScore = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/game/record`, {
        player_name: saveName.trim(),
        mode: "snake",
        duration_seconds: 0,
        score,
        stars,
        level: currentLevel?.name || "All",
        opponent_type: "solo",
      });
      setScoreSaved(true);
    } catch (e) {
      logger.error("Save failed:", e);
    }
    setSaving(false);
  };

  if (!currentLevel && phase === "playing") return null;

  const cellSize = Math.min(56, Math.floor((Math.min(width, 600) - 32) / GRID_COLS));
  const gridW = cellSize * GRID_COLS;
  const gridH = cellSize * GRID_ROWS;
  const lvlColor = currentLevel?.color || "#4ade80";

  return (
    <div className="flex flex-col items-center w-full max-w-5xl mx-auto px-2" data-testid="snake-game-container">
      {phase === "allDone" && <Confetti width={width} height={height} colors={["#FFD700","#FF6B35","#4ECDC4","#FF6B6B","#4ade80"]} recycle={false} numberOfPieces={400} />}

      {/* Top Bar */}
      {(phase === "playing" || phase === "paused") && currentLevel && (
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full mb-2">
          <div className="glass-card-strong rounded-xl p-3 border border-gold/20">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold" style={{ color: lvlColor }}>{currentLevel.icon}</span>
                <span className="font-fredoka text-sm" style={{ color: lvlColor }}>Lv{currentLevel.id} {currentLevel.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-white fill-gold" />
                  <span className="font-fredoka text-white text-sm" data-testid="snake-score">{score}</span>
                </div>
                <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="snake-sound-toggle">
                  {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                </Button>
                <Button onClick={togglePause} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="snake-pause-btn">
                  {phase === "paused" ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                </Button>
                <Button onClick={onBack} variant="outline" size="sm" className="border-gold/20 text-pink-300 hover:bg-gold/10 h-8 px-2" data-testid="snake-back-btn">
                  <Home className="w-3.5 h-3.5 mr-1" />Menu
                </Button>
              </div>
            </div>

            {/* Mission */}
            <div className="rounded-lg p-2 border text-center" style={{ borderColor: `${lvlColor}33`, background: `${lvlColor}10` }}>
              <p className="font-fredoka text-sm sm:text-base font-bold" style={{ color: lvlColor }} data-testid="snake-mission">
                {currentQ?.mission}
              </p>
              {currentLevel.type === "spell_word" && collectedLetters.length > 0 && (
                <div className="flex justify-center gap-1 mt-1">
                  {currentQ?.correct.map((letter, i) => (
                    <span key={i} className="font-fredoka text-sm w-6 h-6 flex items-center justify-center rounded border"
                      style={{
                        borderColor: i < collectedLetters.length ? '#4ade80' : `${lvlColor}33`,
                        color: i < collectedLetters.length ? '#4ade80' : `${lvlColor}55`,
                        background: i < collectedLetters.length ? '#4ade8020' : 'transparent',
                      }}
                    >
                      {i < collectedLetters.length ? collectedLetters[i] : "?"}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Feedback */}
      <AnimatePresence>
        {feedback && (phase === "playing" || phase === "paused") && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className={`mb-2 px-5 py-1.5 rounded-full font-fredoka text-sm font-bold ${
              feedback.type === "correct" || feedback.type === "complete"
                ? "bg-green-500/20 text-green-400 border border-green-500/40"
                : "bg-red-500/20 text-red-400 border border-red-500/40"
            }`}
            data-testid="snake-feedback"
          >
            {feedback.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game Grid */}
      {(phase === "playing" || phase === "paused") && (
        <>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="rounded-xl border-2 relative overflow-hidden"
            style={{
              width: gridW + 2,
              height: gridH + 2,
              background: "rgba(20,15,5,0.85)",
              borderColor: wrongFlash ? "#FF4444" : `${lvlColor}33`,
              boxShadow: wrongFlash ? "0 0 20px #FF444440" : `0 0 20px ${lvlColor}15`,
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
            data-testid="snake-grid"
          >
            {/* Grid lines */}
            <svg width={gridW} height={gridH} className="absolute inset-0 opacity-10">
              {[...Array(GRID_ROWS + 1)].map((_, i) => (
                <line key={`h${i}`} x1={0} y1={i * cellSize} x2={gridW} y2={i * cellSize} stroke={lvlColor} strokeWidth={0.5} />
              ))}
              {[...Array(GRID_COLS + 1)].map((_, i) => (
                <line key={`v${i}`} x1={i * cellSize} y1={0} x2={i * cellSize} y2={gridH} stroke={lvlColor} strokeWidth={0.5} />
              ))}
            </svg>

            {/* Items */}
            {items.map((item, idx) => (
              <motion.div
                key={`item-${idx}-${item.r}-${item.c}-${item.value}`}
                initial={{ scale: 0 }}
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="absolute flex items-center justify-center rounded-lg font-fredoka font-black"
                style={{
                  top: item.r * cellSize + 1,
                  left: item.c * cellSize + 1,
                  width: cellSize - 2,
                  height: cellSize - 2,
                  background: `radial-gradient(circle, ${lvlColor}90, ${lvlColor}50)`,
                  border: `2px solid ${lvlColor}`,
                  color: "#fff",
                  fontSize: cellSize > 30 ? "18px" : "14px",
                  textShadow: `0 0 12px ${lvlColor}, 0 0 6px #fff`,
                  zIndex: 8,
                }}
                data-testid={`snake-item-${idx}`}
              >
                {item.value}
              </motion.div>
            ))}

            {/* Snake */}
            {snake.map((seg, idx) => {
              const isHead = idx === 0;
              const colorIdx = idx % SNAKE_COLORS.length;
              return (
                <motion.div
                  key={`seg-${idx}`}
                  className="absolute rounded-md"
                  animate={isHead && growAnim ? { scale: [1, 1.2, 1] } : {}}
                  style={{
                    top: seg.r * cellSize + 1,
                    left: seg.c * cellSize + 1,
                    width: cellSize - 2,
                    height: cellSize - 2,
                    background: isHead
                      ? `radial-gradient(circle at 40% 40%, #86efac, #22c55e)`
                      : SNAKE_COLORS[colorIdx],
                    boxShadow: isHead ? "0 0 10px #4ade8060" : "none",
                    borderRadius: isHead ? "8px" : "4px",
                    zIndex: isHead ? 10 : 5,
                    transition: "top 0.08s linear, left 0.08s linear",
                  }}
                >
                  {isHead && (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}

            {/* Pause overlay */}
            {phase === "paused" && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-center">
                  <Pause className="w-12 h-12 text-white mx-auto mb-2" />
                  <p className="font-fredoka text-white text-lg">JEDA</p>
                  <Button onClick={togglePause} className="mt-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-brown-dark font-fredoka font-bold" data-testid="snake-resume-btn">
                    <Play className="w-4 h-4 mr-2" />Lanjut
                  </Button>
                </motion.div>
              </div>
            )}
          </motion.div>

          {/* Arrow Controls */}
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mt-3 flex flex-col items-center gap-1">
            <Button onClick={() => changeDir(DIR.UP)} className="w-20 h-14 bg-gradient-to-r from-yellow-400 to-amber-500/20 hover:bg-gradient-to-r from-yellow-400 to-amber-500/30 text-white border border-gold/20 rounded-xl" data-testid="snake-btn-up">
              <ArrowUp className="w-7 h-7" />
            </Button>
            <div className="flex gap-1">
              <Button onClick={() => changeDir(DIR.LEFT)} className="w-20 h-14 bg-gradient-to-r from-yellow-400 to-amber-500/20 hover:bg-gradient-to-r from-yellow-400 to-amber-500/30 text-white border border-gold/20 rounded-xl" data-testid="snake-btn-left">
                <ArrowLeft className="w-7 h-7" />
              </Button>
              <Button onClick={() => changeDir(DIR.DOWN)} className="w-20 h-14 bg-gradient-to-r from-yellow-400 to-amber-500/20 hover:bg-gradient-to-r from-yellow-400 to-amber-500/30 text-white border border-gold/20 rounded-xl" data-testid="snake-btn-down">
                <ArrowDown className="w-7 h-7" />
              </Button>
              <Button onClick={() => changeDir(DIR.RIGHT)} className="w-20 h-14 bg-gradient-to-r from-yellow-400 to-amber-500/20 hover:bg-gradient-to-r from-yellow-400 to-amber-500/30 text-white border border-gold/20 rounded-xl" data-testid="snake-btn-right">
                <ArrowRight className="w-7 h-7" />
              </Button>
            </div>
          </motion.div>

          {/* Question progress */}
          {currentLevel && (
            <div className="mt-2 flex gap-1 justify-center">
              {currentLevel.questions.map((_, i) => (
                <div key={i} className="w-2.5 h-2.5 rounded-full transition-colors"
                  style={{ background: i < qIdx ? '#4ade80' : i === qIdx ? lvlColor : '#3a2a1a' }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Level Up Modal */}
      <AnimatePresence>
        {phase === "levelUp" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
              className="rounded-2xl p-6 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: `${lvlColor}44` }}
            >
              <h2 className="font-fredoka text-xl sm:text-2xl font-bold mb-2" style={{ color: lvlColor }}>
                Level {currentLevel?.id} Selesai!
              </h2>
              <p className="font-fredoka text-gold-light/80 text-sm mb-3">{currentLevel?.name} berhasil!</p>
              <div className="flex justify-center gap-1 mb-4">
                {[1,2,3].map(s => (
                  <Star key={s} className={`w-8 h-8 ${s <= Math.min(3, Math.ceil(stars / Math.max(1, qIdx + 1))) ? 'fill-current' : ''}`}
                    style={{ color: s <= Math.min(3, Math.ceil(stars / Math.max(1, qIdx + 1))) ? '#FFD700' : '#3a2a1a' }} />
                ))}
              </div>
              <p className="font-fredoka text-gold-light/80 text-sm mb-4">
                Skor: <span className="font-bold text-white">{score}</span>
              </p>
              <Button onClick={goNextLevel} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90" data-testid="snake-next-level-btn">
                Lanjut Level Berikutnya!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game Over */}
      <AnimatePresence>
        {phase === "gameover" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }}
              className="rounded-2xl p-6 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: '#FF444444' }}
            >
              <h2 className="font-fredoka text-2xl font-bold text-red-400 mb-2">Nyawa Habis!</h2>
              <p className="font-fredoka text-gold-light/80 text-sm mb-3">Jangan menyerah, coba lagi!</p>
              <p className="font-fredoka text-gold-light/80 text-sm mb-4">
                Skor: <span className="font-bold text-white">{score}</span>
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={restartGame} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90" data-testid="snake-retry-btn">
                  Main Lagi
                </Button>
                <Button onClick={onBack} variant="outline" className="w-full border-gold/20 text-pink-300 hover:bg-gold/10 font-fredoka" data-testid="snake-gameover-back-btn">
                  <Home className="w-4 h-4 mr-2" />Kembali
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* All Done */}
      <AnimatePresence>
        {phase === "allDone" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", damping: 15 }}
              className="rounded-2xl p-6 sm:p-8 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: '#FFD70044', boxShadow: '0 0 60px rgba(255,215,0,0.2)' }}
            >
              <Trophy className="w-16 h-16 mx-auto mb-3 text-white" />
              <h2 className="font-fredoka text-2xl sm:text-3xl font-bold text-white mb-2">Petualangan Selesai!</h2>
              <p className="font-fredoka text-gold-light/80 mb-3">Semua level berhasil ditaklukkan!</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                  <p className="font-fredoka text-gold-light/80 text-xs">Skor</p>
                  <p className="font-fredoka text-2xl text-gold font-bold">{score}</p>
                </div>
                <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                  <p className="font-fredoka text-gold-light/80 text-xs">Bintang</p>
                  <p className="font-fredoka text-2xl text-gold font-bold">{stars}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {!scoreSaved ? (
                  <div className="flex gap-2 mb-1">
                    <input data-testid="snake-save-name-input" type="text" value={saveName}
                      onChange={(e) => setSaveName(e.target.value)} placeholder="Nama kamu..."
                      maxLength={50}
                      className="flex-1 glass-card border border-gold/20 rounded-lg px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-gold/20"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveScore()}
                    />
                    <Button onClick={handleSaveScore} disabled={!saveName.trim() || saving}
                      className="font-fredoka font-bold text-brown-dark px-4" style={{ background: '#4ade80' }}
                      data-testid="snake-save-score-btn">
                      {saving ? '...' : 'Simpan'}
                    </Button>
                  </div>
                ) : (
                  <p className="text-green-400 font-fredoka text-sm mb-1" data-testid="snake-score-saved">Skor tersimpan!</p>
                )}
                <Button onClick={restartGame} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90" data-testid="snake-replay-btn">
                  Main Lagi
                </Button>
                <Button onClick={onBack} variant="outline" className="w-full border-gold/20 text-pink-300 hover:bg-gold/10 font-fredoka" data-testid="snake-finish-btn">
                  <Home className="w-4 h-4 mr-2" />Kembali ke Menu
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SnakeGame;
